import { Link, useLocation } from 'react-router-dom';
import { Ticket, Inbox, TrendingUp, Star, Search, LayoutDashboard } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ROUTES } from '@/lib/routes';

/** Record detail pages light up the list tab they were opened from. */
const TICKET_DETAIL = /^\/psa\/tickets\/\d+$/;
const OPP_DETAIL = /^\/psa\/opportunities\/\d+$/;

/**
 * Tabs stay PSA-only while the other modules are placeholders — five inert tabs
 * would crowd out the ones that work. Revisit when a second module ships.
 *
 * Each tab matches exactly rather than by prefix: `/psa/tickets/queues` and
 * `/psa/tickets/search` are siblings of `/psa/tickets/mine`, so a naive
 * `startsWith('/psa/tickets/')` would light two tabs at once.
 */
const TABS = [
  { to: ROUTES.psa.dashboards.serviceDesk, label: 'Dash',       icon: LayoutDashboard,
    match: (p: string) => p.startsWith(ROUTES.psa.dashboards.root) },
  { to: ROUTES.psa.tickets.mine,       label: 'My Tickets', icon: Ticket,
    match: (p: string) => p === ROUTES.psa.tickets.mine || TICKET_DETAIL.test(p) },
  { to: ROUTES.psa.tickets.queues,     label: 'Queues',     icon: Inbox,
    match: (p: string) => p === ROUTES.psa.tickets.queues },
  { to: ROUTES.psa.opportunities.all,  label: 'All Opps',   icon: TrendingUp,
    match: (p: string) => p === ROUTES.psa.opportunities.all || OPP_DETAIL.test(p) },
  { to: ROUTES.psa.opportunities.mine, label: 'My Opps',    icon: Star,
    match: (p: string) => p === ROUTES.psa.opportunities.mine },
  { to: ROUTES.psa.search,             label: 'Search',     icon: Search,
    match: (p: string) => p === ROUTES.psa.search },
] as const;

/**
 * Mobile-only bottom tab bar. The active tab gets a small "pill" backdrop
 * behind the icon — subtle, but reads more like a polished app and less like
 * a plain CSS row.
 */
export function BottomNav() {
  const { pathname } = useLocation();

  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-50 flex border-t border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80 md:hidden"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {TABS.map(({ to, label, icon: Icon, match }) => {
        const active = match(pathname);
        return (
          <Link
            key={to}
            to={to}
            className={cn(
              'group flex flex-1 flex-col items-center gap-1 px-1 pt-1.5 pb-2 text-[10.5px] font-medium transition-colors',
              active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <span
              className={cn(
                'inline-flex h-7 w-12 items-center justify-center rounded-full transition-colors',
                active ? 'bg-brand-soft text-brand-strong dark:text-brand' : 'group-hover:bg-muted',
              )}
            >
              <Icon className={cn('h-[18px] w-[18px]', active && 'stroke-[2.25]')} />
            </span>
            <span className="tracking-[0.01em]">{label}</span>
          </Link>
        );
      })}
    </nav>
  );
}
